import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactSelect, { SingleValue } from 'react-select';
import { Heading, Subheading } from '../../../../../components/Heading';
import { Card } from '../../../../../components/Card';
import { Input } from '../../../../../components/Input';
import { Select } from '../../../../../components/Select';
import { Button } from '../../../../../components/Button';
import showLoadingToast from '../../../../../components/Toasts/ShowLoadingToast';
import showSuccessToast from '../../../../../components/Toasts/ShowSuccessToast';
import showErrorToast from '../../../../../components/Toasts/ShowErrorToast';
import { useSetupUsergroup } from '../../../../../hooks/useSetupUsergroup';
import { useWarehouses } from '../../../../../hooks/useWarehouse';
import { useWarehouseProduct } from '../../../../../hooks/useWarehouseProduct';
import { useTrackingOrder } from '../../../../../hooks/useTrackingOrder';
import { UserContext } from '../../../../../context/userContext';
import { TrackingOrderItem } from '../../../../../models/TrackingOrderItem';
import { Text } from '../../../../../components/Text';
import { Dialog, DialogActions, DialogDescription, DialogTitle } from '../../../../../components/Dialog';
import Loading from '../../../../../components/Loading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../../components/Table';

interface AddTrackingOrderItem {
    product_id: number | null;
    description: string;
    quantity: string;
    uom: string;
    is_other: boolean;
}

const EditTrackingOrderPage = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { updateTrackingOrder, fetchTrackingOrderById, loading, error } = useTrackingOrder();
    const { customers } = useSetupUsergroup();
    const { warehouses } = useWarehouses();
    const { products, fetchAllFilteredProduct } = useWarehouseProduct();
    const { currentUser } = useContext(UserContext);
    const filteredWarehouses = warehouses.filter(w => w.hq !== '1');

    const [order, setOrder] = useState({ customer_id: 0, warehouse_id: 0, delivery_number: '', remark: '' });
    const [itemForm, setItemForm] = useState<AddTrackingOrderItem>({
        product_id: 0, description: '', quantity: '1', uom: '', is_other: false,
    });
    const [itemsList, setItemsList] = useState<AddTrackingOrderItem[]>([]);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [editItemIndex, setEditItemIndex] = useState<number | null>(null);
    const [editForm, setEditForm] = useState<Partial<AddTrackingOrderItem>>({});

    useEffect(() => {
        document.title = "Edit Tracking Order";
    }, []);

    useEffect(() => {
        fetchAllFilteredProduct?.();
    }, []);

    useEffect(() => {
        const load = async () => {
            if (!id) return;
            const o = await fetchTrackingOrderById(Number(id));
            if (o) {
                setOrder({
                    customer_id: o.customer_id,
                    warehouse_id: o.warehouse_id || 0,
                    delivery_number: o.delivery_number || '',
                    remark: o.remark || ''
                });
                if (o.items && o.items.length > 0) {
                    const list: AddTrackingOrderItem[] = o.items.map((it: any) => ({
                        product_id: it.product_id || 0,
                        description: it.description || '',
                        quantity: String(it.quantity || 1),
                        uom: it.uom || '',
                        is_other: !!it.is_other,
                    }));
                    setItemsList(list);
                }
            }
        };
        load();
    }, [id]);

    const handleOrderChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        if (name === 'delivery_number' || name === 'remark') {
            setOrder(prev => ({ ...prev, [name]: value }));
        } else {
            setOrder(prev => ({ ...prev, [name]: Number(value) }));
        }
    };

    const selectProductOptions = products.map(p => ({
        value: p.id!,
        label: `${p.item_no} - ${p.description}`,
    }));

    const selectCustomerOptions = customers.map(c => ({
        value: Number(c.id),
        label: c.fullname ?? '',
    }));

    const handleCustomerChange = (newValue: SingleValue<{ value: number; label: string }>) => {
        const cid = newValue?.value ?? 0;
        setOrder(prev => ({ ...prev, customer_id: cid }));
    };

    const handleProductChange = (newValue: SingleValue<{ value: number; label: string }>) => {
        const id = newValue?.value ?? 0;
        setItemForm(prev => ({ ...prev, product_id: id, is_other: false }));
    };

    const handleItemChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;
        setItemForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        if (name === 'is_other' && checked) setItemForm(prev => ({ ...prev, product_id: 0, description: '' }));
    };

    const handleAddToTable = () => {
        const needsProduct = !itemForm.is_other && (!itemForm.product_id || itemForm.product_id === 0);
        const needsDescription = itemForm.is_other && !itemForm.description?.trim();
        if (needsProduct || needsDescription) {
            showErrorToast('Select a product or check Others and enter description.');
            return;
        }
        if (!itemForm.quantity || Number(itemForm.quantity) <= 0) {
            showErrorToast('Enter a valid quantity.');
            return;
        }
        setItemsList(prev => [...prev, { ...itemForm }]);
        setItemForm({ product_id: 0, description: '', quantity: '1', uom: '', is_other: false });
        if (products.length > 0) setItemForm(prev => ({ ...prev, product_id: Number(products[0].id) }));
    };

    const handleDeleteItem = (index: number) => setItemsList(prev => prev.filter((_, i) => i !== index));

    const handleOpenEdit = (index: number) => {
        setEditForm(itemsList[index]);
        setEditItemIndex(index);
        setIsEditOpen(true);
    };

    const handleEditSubmit = () => {
        if (editItemIndex !== null) {
            const next = [...itemsList];
            next[editItemIndex] = { ...next[editItemIndex], ...editForm };
            setItemsList(next);
            setIsEditOpen(false);
        }
    };

    const handleEditProductChange = (newValue: SingleValue<{ value: number; label: string }>) => {
        const id = newValue?.value ?? 0;
        setEditForm(prev => ({ ...prev, product_id: id, is_other: false }));
    };

    const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;
        setEditForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const getItemDisplay = (item: AddTrackingOrderItem) => {
        if (item.is_other) return item.description || '-';
        const p = products.find(pr => Number(pr.id) === Number(item.product_id));
        return p?.description || p?.item_no || '-';
    };

    const toApiItems = (): TrackingOrderItem[] => itemsList.map(it => ({
        tracking_order_id: 0,
        product_id: it.is_other ? null : it.product_id,
        description: it.is_other ? it.description : undefined,
        quantity: it.quantity,
        uom: it.uom || undefined,
        is_other: it.is_other,
    }));

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!id) return;
        if (!order.customer_id || order.customer_id === 0) {
            showErrorToast('Please select a customer.');
            return;
        }
        try {
            showLoadingToast('Updating tracking order...', 100);
            const res = await updateTrackingOrder(
                Number(id),
                {
                    customer_id: order.customer_id,
                    warehouse_id: order.warehouse_id || null,
                    delivery_number: order.delivery_number || null,
                    remark: order.remark || null
                },
                toApiItems()
            );
            if (res?.dbsuccess) {
                showSuccessToast('Tracking order updated successfully');
                navigate('/gp/tracking/standalone');
            } else {
                showErrorToast('Failed to update tracking order');
            }
        } catch (err) {
            showErrorToast('Failed to update tracking order');
        }
    };

    if (loading) return <Loading />;
    if (error) return <p className="py-16 text-center text-red-700">{error}</p>;

    return (
        <form method="post" onSubmit={handleSubmit} className="mx-auto max-w-6xl">
            <div className="lg:flex lg:items-center lg:justify-between mb-5">
                <Heading>Edit Tracking Order</Heading>
                <div className="mt-5 flex lg:ml-4 lg:mt-0">
                    <Button type="button" outline onClick={() => navigate(-1)}>Cancel</Button>
                    <Button type="submit" className="ml-3" disabled={!order.customer_id}>Save</Button>
                </div>
            </div>
            <Card className="mt-6 p-6">
                <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
                    <div>
                        <h2 className="text-base font-semibold leading-7 text-foreground">Order Information</h2>
                    </div>
                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                        <div className="sm:col-span-full">
                            <Subheading>Customer</Subheading>
                            <ReactSelect
                                options={selectCustomerOptions}
                                placeholder="Select customer"
                                value={selectCustomerOptions.find(o => Number(o.value) === Number(order.customer_id)) ?? null}
                                onChange={handleCustomerChange}
                                isClearable
                                className="text-sm"
                            />
                        </div>
                        <div className="sm:col-span-full">
                            <Subheading>Warehouse</Subheading>
                            <Select name="warehouse_id" value={order.warehouse_id.toString()} onChange={handleOrderChange} disabled={currentUser?.warehouse_hq !== "1"}>
                                {filteredWarehouses.map(w => (
                                    <option key={w.id} value={w.id!.toString()}>{w.warehouse_name}</option>
                                ))}
                            </Select>
                        </div>
                        <div className="sm:col-span-full">
                            <Subheading>Delivery Number</Subheading>
                            <Input
                                name="delivery_number"
                                value={order.delivery_number}
                                onChange={handleOrderChange}
                                placeholder="Enter delivery number"
                            />
                        </div>
                        <div className="sm:col-span-full">
                            <Subheading>Remark</Subheading>
                            <Input
                                name="remark"
                                value={order.remark}
                                onChange={handleOrderChange}
                                placeholder="Enter remark (optional)"
                            />
                        </div>
                    </div>
                </div>
            </Card>
            <Card className="mt-6 p-6">
                <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
                    <div>
                        <h2 className="text-base font-semibold leading-7 text-foreground">Items</h2>
                    </div>
                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                        <div className="sm:col-span-full flex items-center gap-4">
                            <label className="flex items-center gap-2">
                                <input type="checkbox" name="is_other" checked={itemForm.is_other} onChange={handleItemChange} />
                                <Text>Others</Text>
                            </label>
                        </div>
                        {!itemForm.is_other ? (
                            <div className="sm:col-span-full">
                                <Subheading>Product</Subheading>
                                <ReactSelect
                                    options={selectProductOptions}
                                    placeholder="Select product"
                                    value={selectProductOptions.find(o => Number(o.value) === Number(itemForm.product_id)) ?? null}
                                    onChange={handleProductChange}
                                    isClearable
                                    className="text-sm"
                                />
                            </div>
                        ) : (
                            <div className="sm:col-span-full">
                                <Subheading>Description</Subheading>
                                <Input name="description" value={itemForm.description} onChange={handleItemChange} placeholder="Enter description" />
                            </div>
                        )}
                        <div className="sm:col-span-3">
                            <Subheading>Quantity</Subheading>
                            <Input type="number" name="quantity" value={itemForm.quantity} onChange={handleItemChange} min="0" step="0.01" />
                        </div>
                        <div className="sm:col-span-3">
                            <Subheading>UOM</Subheading>
                            <Input name="uom" value={itemForm.uom} onChange={handleItemChange} placeholder="e.g. box, pcs" />
                        </div>
                        <div className="sm:col-span-full">
                            <Button type="button" onClick={handleAddToTable}>Add to Table</Button>
                        </div>
                    </div>
                </div>
            </Card>
            {itemsList.length > 0 && (
                <Card className="mt-6 p-6">
                    <Subheading>Added Items</Subheading>
                    <Table className="mt-4 [--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                        <TableHead>
                            <TableRow>
                                <TableHeader>Description</TableHeader>
                                <TableHeader>Quantity</TableHeader>
                                <TableHeader>UOM</TableHeader>
                                <TableHeader>Actions</TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {itemsList.map((item, idx) => (
                                <TableRow key={idx} title={`${idx}`}>
                                    <TableCell>{getItemDisplay(item)}</TableCell>
                                    <TableCell className="text-zinc-500">{item.quantity}</TableCell>
                                    <TableCell className="text-zinc-500">{item.uom || '-'}</TableCell>
                                    <TableCell className="py-5 flex gap-4">
                                        <Button plain className="text-primary" onClick={() => handleOpenEdit(idx)}>Edit</Button>
                                        <Button plain className="text-primary" onClick={() => handleDeleteItem(idx)}>Delete</Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            )}
            <div className="flex justify-end gap-4 my-6">
                <Button type="button" plain onClick={() => navigate(-1)}>Cancel</Button>
                <Button type="submit" disabled={!order.customer_id}>Save</Button>
            </div>
            <Dialog open={isEditOpen} onClose={() => setIsEditOpen(false)}>
                <DialogTitle>Edit Item</DialogTitle>
                <DialogDescription>Update the item details.</DialogDescription>
                <div className="grid gap-4 mt-4">
                    {editForm.is_other ? (
                        <div>
                            <Subheading>Description</Subheading>
                            <Input name="description" value={editForm.description || ''} onChange={handleEditChange} />
                        </div>
                    ) : (
                        <div>
                            <Subheading>Product</Subheading>
                            <ReactSelect
                                options={selectProductOptions}
                                placeholder="Select product"
                                value={selectProductOptions.find(o => Number(o.value) === Number(editForm.product_id)) ?? null}
                                onChange={handleEditProductChange}
                                isClearable
                                className="text-sm"
                            />
                        </div>
                    )}
                    <div>
                        <Subheading>Quantity</Subheading>
                        <Input type="number" name="quantity" value={editForm.quantity || ''} onChange={handleEditChange} />
                    </div>
                    <div>
                        <Subheading>UOM</Subheading>
                        <Input name="uom" value={editForm.uom || ''} onChange={handleEditChange} />
                    </div>
                </div>
                <DialogActions>
                    <Button plain onClick={() => setIsEditOpen(false)}>Cancel</Button>
                    <Button onClick={handleEditSubmit}>Save</Button>
                </DialogActions>
            </Dialog>
        </form>
    );
};

export default EditTrackingOrderPage;
